import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';

function CommercialRealEstate() {
  return (
    <div className="pt-28">
      {/* Hero Section with Background Image */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
        ></div>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Building2 className="w-16 h-16 text-gold mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Commercial <span className="text-gold">Real Estate</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Office buildings, mixed-use developments, and corporate headquarters
          </p>
        </div>
      </section>

      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              Commercial real estate projects demand precision, quality craftsmanship, and professional project management. Williams Diversified delivers comprehensive construction services for office buildings, corporate campuses, mixed-use developments, and commercial properties of all scales.
            </p>
            <p>
              From ground-up construction to tenant improvements and renovations, we bring expertise in delivering commercial projects on time and within budget while maintaining the highest standards of quality.
            </p>
          </div>
        </div>
      </section>

      {/* Facility Maintenance Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-8 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Commercial Property <span className="text-gold">Facility Maintenance</span>
          </h3>
          <p className="text-gray-300 text-lg leading-relaxed text-center mb-8">
            Protect your investment and keep tenants satisfied with comprehensive facility maintenance services for office buildings and commercial properties.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Building Systems Maintenance</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>HVAC preventive maintenance</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Elevator & escalator service</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Electrical system inspections</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Plumbing & water systems</li>
              </ul>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Property Management Support</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Common area maintenance</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Parking structure repairs</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Roof maintenance & repairs</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Tenant improvement coordination</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* National Rollout Programs Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            National <span className="text-gold">Rollout Programs</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Scalable construction solutions for commercial real estate portfolios requiring consistent quality across multiple properties
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">50+</div>
              <div className="text-gray-300 font-medium mb-2">States Covered</div>
              <p className="text-gray-500 text-sm">Nationwide service capability with local expertise</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">1</div>
              <div className="text-gray-300 font-medium mb-2">Point of Contact</div>
              <p className="text-gray-500 text-sm">Single account manager for all locations</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">100%</div>
              <div className="text-gray-300 font-medium mb-2">Brand Consistency</div>
              <p className="text-gray-500 text-sm">Standardized execution across every property</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">24/7</div>
              <div className="text-gray-300 font-medium mb-2">Support</div>
              <p className="text-gray-500 text-sm">Round-the-clock emergency response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Why Choose Us for <span className="text-gold">Commercial Real Estate</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Building premier commercial properties that maximize value
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">01</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Value Engineering</h4>
                <p className="text-gray-400 text-sm mb-4">Strategic cost optimization without compromising quality. We identify savings opportunities while maintaining design intent and building performance.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">02</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Tenant Coordination</h4>
                <p className="text-gray-400 text-sm mb-4">Expert management of tenant improvements and build-outs. We work seamlessly with property managers and tenants to minimize disruption.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">03</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Schedule Certainty</h4>
                <p className="text-gray-400 text-sm mb-4">On-time delivery is critical for lease-up and occupancy. Our proven project management ensures your building opens when promised.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-12 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Our Commercial <span className="text-gold">Construction Process</span>
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">1</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Pre-Construction</h4>
              <p className="text-gray-400 text-sm">Budget development, value engineering, and constructability review to optimize your investment.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">2</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Design Coordination</h4>
              <p className="text-gray-400 text-sm">Collaboration with architects and engineers to ensure buildability and code compliance.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">3</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Construction</h4>
              <p className="text-gray-400 text-sm">Professional execution with regular progress updates, quality control, and safety management.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">4</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Tenant Turnover</h4>
              <p className="text-gray-400 text-sm">Punch list completion, systems commissioning, and smooth handover for occupancy.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Build Your <span className="text-gold">Vision</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Partner with a contractor who understands commercial real estate
          </p>
          <Link to="/contact" className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}

export default CommercialRealEstate;
